"use client";

import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import { Text } from "@lmn/design-system";
import IconCheck from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconCheck.js";
import ChipDropdown from "@/components/UI/ChipDropdown";
import PriceFilter from "@/components/Stateful/Filters/ASN/PriceFilter/PriceFilter";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";
import SpecialDealsFilter from "@/components/Stateful/Filters/ASN/SpecialDealsFilter/SpecialDealsFilter";
import RoomTypeFilter from "@/components/Stateful/Filters/ASN/RoomTypeFilter/RoomTypeFilter";
import TransferFilter from "@/components/Stateful/Filters/ASN/TransferFilter/TransferFilter";
import RentalCarFilter from "@/components/Stateful/Filters/ASN/TransferFilter/RentalCarFilter";
import FlexibleCancellationFilter from "@/components/Stateful/Filters/ASN/FlexibleCancellationFilter/FlexibleCancellationFilter";
import BookOnOptionFilter from "@/components/Stateful/Filters/ASN/BookOnOptionFilter/BookOnOptionFilter";
import TourOperatorFilter from "@/components/Stateful/Filters/ASN/TourOperatorFilter/TourOperatorFilter";
import AirlineFilter from "@/components/Stateful/Filters/ASN/AirlineFilter/AirlineFilter";
import initialState from "@/lib/StaticData/searchFormData";
import { hotelDetailSortByOptions } from "@/lib/StaticData/sortOptions";
import { useSortLogic } from "@/lib/Hooks/useSortLogic";
import { channel } from "@/lib/Types/Channel";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import { AppConfig } from "@/lib/Types/AppConfig";
import useAirlines from "@/app/api/useAirlines";

const Container = styled.div`
  display: flex;
  gap: 8px;
  padding: 1rem 0 2rem 0;
  overflow-x: auto;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  margin: 1rem 0;
  background-color: ${({ theme }) =>
    theme.kallisto.color.background.static.backgroundDisabled};
`;

const SortOption = styled.div`
  cursor: pointer;
  border-bottom: 1px solid
    ${({ theme }) =>
    theme.kallisto.color.background.interactive.backgroundNeutralHover};

  &:last-child {
    border-bottom: none;
  }
`;

const SortOptionContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;

  &:hover {
    background: ${({ theme }) =>
    theme.kallisto.color.background.interactive.backgroundNeutralHover};
  }
`;

const StyledIconCheck = styled(IconCheck)`
  color: ${({ theme }) =>
    theme.kallisto.color.icon.interactive.iconPrimaryDefault};
`;

//Filter group configuration type
interface FilterGroup {
  id: string;
  label: string;
  keys: readonly string[];
  isActive: (params: URLSearchParams) => boolean;
  resetKeys: readonly string[];
  resetCategories?: readonly number[];
  commaSeparatedKeys?: readonly string[];
  scrollable?: boolean;
  children: React.ReactNode;
}

interface DesktopFiltersProps {
  appConfig: AppConfig;
}

export default function DesktopFilters({ appConfig }: DesktopFiltersProps) {
  const searchParams = useReactiveSearchParams();
  const { commitFilters, resetTempFilters, removeFilters, tempFilters } =
    useFilterState();
  const { viewMode, WlConfig } = appConfig;
  const BOOKONOPTION_ENABLED = true;
  const hasFlexibleCancellation = WlConfig.hasFlexibleCancellation.hotelDetail;
  const isHotelOnly = viewMode.channel === channel.HOTEL;

  useAirlines(appConfig);

  const sortOptions =
    isHotelOnly
      ? hotelDetailSortByOptions.filter(({ value }) => value !== "AIRPORT")
      : hotelDetailSortByOptions;

  const { selectedSort, setSelectedSort, currentOption } = useSortLogic(
    sortOptions,
    sortOptions[0],
    appConfig.envConfig
  );

  const sortLabel = `Sortiert nach ${currentOption?.label ?? sortOptions[0].label}`;

  // Filter groups configuration - defines all filter dropdowns
  const FILTER_GROUPS: FilterGroup[] = useMemo(
    () => [
      {
        id: "roomType",
        label: "Zimmer",
        keys: ["roomType", "oceanView", "categories"],
        isActive: (params: URLSearchParams) => {
          const roomType = params.get("roomType");
          const oceanView = params.get("oceanView");
          const categories = params.get("categories");
          const roomTypeCategoryIds = initialState.roomTypeCategories.allIds.map(String);
          const hasRoomTypeCategory =
            categories !== null &&
            categories !== "" &&
            categories
              .split(",")
              .some((cat) => roomTypeCategoryIds.includes(cat));
          return (
            (roomType !== null && roomType !== "") ||
            oceanView === "true" ||
            hasRoomTypeCategory
          );
        },
        resetKeys: ["roomType", "oceanView"],
        resetCategories: initialState.roomTypeCategories.allIds,
        commaSeparatedKeys: ["roomType"],
        children: <RoomTypeFilter delayedUpdate expanded />,
      },
      {
        id: "price",
        label: "Preis",
        keys: ["price", "specialDeals"],
        isActive: (params: URLSearchParams) => {
          const price = params.get("price");
          const specialDeals = params.get("specialDeals");
          return (
            (price !== null && price !== "6000") || specialDeals === "true"
          );
        },
        resetKeys: ["price", "specialDeals"],
        children: (
          <>
            <SpecialDealsFilter delayedUpdate onWhiteBackground />
            <Divider />
            <PriceFilter delayedUpdate />
          </>
        ),
      },
      isHotelOnly
        ? {
            id: "transfer",
            label: "Transfer",
            keys: ["rentalCar"],
            isActive: (params: URLSearchParams) =>
              params.get("rentalCar") === "1",
            resetKeys: ["rentalCar"],
            children: <RentalCarFilter delayedUpdate />,
          }
        : {
            id: "transfer",
            label: "Flug & Transfer",
            keys: [
              "directFlight",
              "railAndFly",
              "transfer",
              "rentalCar",
              "departureFlightTimeFrom",
              "departureFlightTimeUntil",
              "returnFlightTimeFrom",
              "returnFlightTimeUntil",
            ],
            isActive: (params: URLSearchParams) => {
              return (
                params.get("directFlight") === "1" ||
                params.get("railAndFly") === "1" ||
                params.get("transfer") === "1" ||
                params.get("rentalCar") === "1" ||
                (params.get("departureFlightTimeFrom") !== null &&
                  params.get("departureFlightTimeFrom") !== "0") ||
                (params.get("departureFlightTimeUntil") !== null &&
                  params.get("departureFlightTimeUntil") !== "1440") ||
                (params.get("returnFlightTimeFrom") !== null &&
                  params.get("returnFlightTimeFrom") !== "0") ||
                (params.get("returnFlightTimeUntil") !== null &&
                  params.get("returnFlightTimeUntil") !== "1440")
              );
            },
            resetKeys: [
              "directFlight",
              "railAndFly",
              "transfer",
              "rentalCar",
              "departureFlightTimeFrom",
              "departureFlightTimeUntil",
              "returnFlightTimeFrom",
              "returnFlightTimeUntil",
            ],
            children: (
              <TransferFilter delayedUpdate isHotelDetailPage expanded />
            ),
          },
      ...(!isHotelOnly
        ? [
            {
              id: "airline",
              label: "Airline",
              keys: ["airline"],
              isActive: (params: URLSearchParams) => {
                const airline = params.get("airline");
                return airline !== null && airline !== "";
              },
              resetKeys: ["airline"],
              commaSeparatedKeys: ["airline"],
              scrollable: true,
              children: (
                <AirlineFilter delayedUpdate expanded appConfig={appConfig} />
              ),
            },
          ]
        : []),
      ...((hasFlexibleCancellation || BOOKONOPTION_ENABLED) ? [{
        id: "sorgenfrei",
        label: "Sorgenfrei buchen",
        keys: ["flexibleOffers", "bookOnOption"],
        isActive: (params: URLSearchParams) => {
          return (
            params.get("flexibleOffers") === "flexibleCancellation" ||
            params.get("bookOnOption") === "true"
          );
        },
        resetKeys: ["flexibleOffers", "bookOnOption"],
        children: (
          <>
            {hasFlexibleCancellation && (
              <FlexibleCancellationFilter delayedUpdate onWhiteBackground />
            )}
            {BOOKONOPTION_ENABLED && (
              <BookOnOptionFilter delayedUpdate onWhiteBackground />
            )}
          </>
        ),
      }] : []),
      {
        id: "tourOperator",
        label: "Reiseveranstalter",
        keys: ["tourOperator"],
        isActive: (params: URLSearchParams) => {
          const tourOperator = params.get("tourOperator");
          return tourOperator !== null && tourOperator !== "";
        },
        resetKeys: ["tourOperator"],
        commaSeparatedKeys: ["tourOperator"],
        scrollable: true,
        children: <TourOperatorFilter delayedUpdate expanded appConfig={appConfig} />,
      },
    ],
    [hasFlexibleCancellation, BOOKONOPTION_ENABLED, appConfig, isHotelOnly]
  );

  // Check if specific filter keys have pending changes
  const hasPendingChangesForKeys = useCallback(
    (keys: readonly string[]) => {
      return keys.some((key) => {
        const urlValue = searchParams.get(key);
        const tempValue = tempFilters.get(key);
        return urlValue !== tempValue;
      });
    },
    [searchParams, tempFilters]
  );

  // Generic reset handler for all filter groups
  const handleReset = useCallback(
    (group: FilterGroup) => {
      const filtersToRemove: Array<{ key: string; value: string }> = [];
      const commaSeparatedKeys = group.commaSeparatedKeys ?? [];

      group.resetKeys.forEach((key) => {
        const value = searchParams.get(key);
        if (!value) return;

        if (commaSeparatedKeys.includes(key)) {
          // Split comma-separated values
          value.split(",").forEach((v) => {
            filtersToRemove.push({ key, value: v });
          });
        } else {
          // Regular single value
          filtersToRemove.push({ key, value });
        }
      });

      // Handle special category reset if defined
      if (group.resetCategories) {
        group.resetCategories.forEach((id) => {
          filtersToRemove.push({ key: "categories", value: String(id) });
        });
      }

      if (filtersToRemove.length > 0) {
        removeFilters(filtersToRemove);
      }
    },
    [searchParams, removeFilters]
  );

  return (
    <>
      <Container>
        {FILTER_GROUPS.map((group) => (
          <ChipDropdown
            key={group.id}
            label={group.label}
            isFilterActive={group.isActive(searchParams)}
            onConfirm={commitFilters}
            onCancel={resetTempFilters}
            onReset={() => handleReset(group)}
            hasPendingChanges={hasPendingChangesForKeys(group.keys)}
            scrollable={group.scrollable}
          >
            {group.children}
          </ChipDropdown>
        ))}

        {/* Sort dropdown - separate behavior (immediateApply, render function) */}
        <div style={{ marginLeft: "auto" }}>
          <ChipDropdown label={sortLabel} immediateApply>
            {(closeMenu) => (
              <>
                {sortOptions.map((option) => (
                  <SortOption
                    key={option.value}
                    onClick={() => {
                      setSelectedSort(option.value);
                      closeMenu();
                    }}
                  >
                    <SortOptionContent>
                      <Text variant="BodySmall">{option.label}</Text>
                      {selectedSort === option.value && <StyledIconCheck />}
                    </SortOptionContent>
                  </SortOption>
                ))}
              </>
            )}
          </ChipDropdown>
        </div>
      </Container>
    </>
  );
}
